/**
 * useDisplayName — numele ales de user, persistat per-device.
 *
 * Strategie:
 *  1. La mount, citeste din localStorage pentru valoare instant (fara loading state)
 *  2. In paralel, citeste din Supabase `visitors.display_name` pentru a suprascrie
 *     cache-ul local daca device-ul e nou sau userul a schimbat browserul
 *  3. setName(name) scrie in ambele: localStorage + Supabase
 *  4. clearName() sterge din ambele
 *
 * Folosit de:
 *  - UploadForm: alege automat numele la upload (fara input daca e salvat)
 *  - CommentList: la fel pentru autor
 */

import { useCallback, useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { getDeviceId } from '@/lib/device';
import { syncUploaderName } from '@/lib/api';

const LS_KEY = 'guestlens_display_name';

function readLocal(): string {
  if (typeof window === 'undefined') return '';
  try {
    return localStorage.getItem(LS_KEY) ?? '';
  } catch {
    return '';
  }
}

function writeLocal(name: string) {
  if (typeof window === 'undefined') return;
  try {
    if (!name) localStorage.removeItem(LS_KEY);
    else localStorage.setItem(LS_KEY, name);
  } catch {
    // localStorage plin / disabled
  }
}

async function fetchRemote(): Promise<string> {
  const deviceId = getDeviceId();
  if (!deviceId) return '';
  try {
    const { data, error } = await supabase
      .from('visitors')
      .select('display_name')
      .eq('device_id', deviceId)
      .maybeSingle();
    if (error) {
      // eslint-disable-next-line no-console
      console.warn('[useDisplayName] fetch failed:', error.message);
      return '';
    }
    return data?.display_name ?? '';
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn('[useDisplayName] fetch exception:', e);
    return '';
  }
}

async function persistRemote(name: string): Promise<void> {
  const deviceId = getDeviceId();
  if (!deviceId) return;
  const { error } = await supabase
    .from('visitors')
    .update({ display_name: name || null })
    .eq('device_id', deviceId);
  if (error) throw error;
}

export function useDisplayName() {
  // Initial: localStorage instant (nu blocam UI-ul cu loading)
  const [name, setNameState] = useState<string>(() => readLocal());
  const [loading, setLoading] = useState(true);

  // La mount: sincronizeaza cu Supabase
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const remote = await fetchRemote();
      if (cancelled) return;
      const local = readLocal();
      // Castiga remote daca e setat, altfel pastram local
      const next = remote || local;
      if (next !== local) {
        writeLocal(next);
        setNameState(next);
      }
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const setName = useCallback(async (newName: string): Promise<void> => {
    const trimmed = newName.trim();
    const prev = name;
    writeLocal(trimmed);
    setNameState(trimmed);
    // Actualizează remote display_name și sincronizează pozele vechi
    try {
      await Promise.all([
        persistRemote(trimmed),
        syncUploaderName(trimmed, prev),
      ]);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn('[useDisplayName] persist remote failed:', e);
    }
  }, [name]);

  const clearName = useCallback(async () => {
    writeLocal('');
    setNameState('');
    try {
      await persistRemote('');
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn('[useDisplayName] clear remote failed:', e);
    }
  }, []);

  return { name, loading, setName, clearName };
}

/**
 * Fallback pentru upload-uri/comentarii cand userul nu a setat nume.
 */
export const ANONYMOUS_NAME = 'Anonim';
